import { Cigar, CIGARS, searchCigars, getCigarsByStrength, getCigarsByFlavor } from '../data/cigars';

export type IntentType = 'search' | 'command' | 'log' | 'unknown';

export interface VoiceIntent {
  type: IntentType;
  confidence: number;
  raw: string;
}

export interface SearchIntent extends VoiceIntent {
  type: 'search';
  query: string;
  filters: {
    strength?: string;
    flavor?: string;
    brand?: string;
  };
  results: Cigar[];
}

export interface CommandIntent extends VoiceIntent {
  type: 'command';
  command: string;
  target: string;
}

export interface LogIntent extends VoiceIntent {
  type: 'log';
  cigarName?: string;
  rating?: number;
  notes?: string;
}

export interface UnknownIntent extends VoiceIntent {
  type: 'unknown';
  suggestion: string;
}

export type ParsedIntent = SearchIntent | CommandIntent | LogIntent | UnknownIntent;

// Command patterns
const COMMANDS: { patterns: RegExp[]; target: string; command: string }[] = [
  {
    patterns: [/show\s*(my)?\s*humidor/i, /open\s*(my)?\s*humidor/i, /my\s*humidor/i],
    target: 'humidor',
    command: 'navigate'
  },
  {
    patterns: [/today'?s?\s*smoke/i, /what'?s?\s*today/i, /daily\s*smoke/i],
    target: 'dashboard',
    command: 'todaysSmoke'
  },
  {
    patterns: [/show\s*(my)?\s*journal/i, /open\s*(my)?\s*journal/i, /my\s*journal/i],
    target: 'journal',
    command: 'navigate'
  },
  {
    patterns: [/find\s*lounge/i, /lounge/i, /cigar\s*bar/i, /where\s*can\s*i\s*smoke/i],
    target: 'lounges',
    command: 'navigate'
  },
  {
    patterns: [/go\s*home/i, /show\s*dashboard/i, /main\s*screen/i],
    target: 'dashboard',
    command: 'navigate'
  },
  {
    patterns: [/cigar\s*explorer/i, /browse\s*cigars/i, /explore\s*cigars/i],
    target: 'explorer',
    command: 'navigate'
  },
  {
    patterns: [/band\s*gallery/i, /my\s*bands/i, /cigar\s*bands/i],
    target: 'band-gallery',
    command: 'navigate'
  },
  {
    patterns: [
      /identify\s*(this)?\s*(cigar)?/i,
      /what\s*(cigar\s*)?(is\s*)?this/i,
      /scan\s*(this)?\s*(cigar|band)?/i,
      /what'?s?\s*this\s*(cigar)?/i,
      /recognize\s*(this)?\s*(cigar)?/i,
    ],
    target: 'identify',
    command: 'camera'
  },
];

// Strength keywords
const STRENGTH_KEYWORDS: { [key: string]: string } = {
  'mild': 'mild',
  'light': 'mild',
  'mellow': 'mild',
  'medium': 'medium',
  'moderate': 'medium',
  'full': 'full',
  'strong': 'full',
  'bold': 'full',
  'heavy': 'full',
};

// Flavor keywords
const FLAVOR_KEYWORDS = [
  'coffee', 'chocolate', 'cocoa', 'espresso',
  'leather', 'earth', 'earthy', 'woody', 'cedar',
  'pepper', 'spicy', 'spice',
  'cream', 'creamy', 'vanilla', 'sweet',
  'nuts', 'nutty', 'almond',
  'citrus', 'floral', 'hay', 'toast',
];

export function parseVoiceIntent(transcript: string): ParsedIntent {
  const text = transcript.toLowerCase().trim();

  // 1. Check for commands first
  for (const cmd of COMMANDS) {
    for (const pattern of cmd.patterns) {
      if (pattern.test(text)) {
        return {
          type: 'command',
          command: cmd.command,
          target: cmd.target,
          confidence: 0.9,
          raw: transcript,
        };
      }
    }
  }

  // 2. Check for log intent
  if (/log\s*(this|a|my)?\s*(smoke|cigar)?/i.test(text) ||
      /rate\s*(this|a)?\s*(cigar)?/i.test(text) ||
      /smoking\s*(a|the)?/i.test(text)) {
    const logIntent: LogIntent = {
      type: 'log',
      confidence: 0.8,
      raw: transcript,
    };

    // Try to extract rating (number between 1-100 or 1-10)
    const ratingMatch = text.match(/rat(?:ed?|ing)\s*(?:it\s*)?(\d{1,3})/i) ||
                        text.match(/(\d{1,3})\s*(?:out\s*of|\/)\s*(?:100|10)/i) ||
                        text.match(/score\s*(?:of\s*)?(\d{1,3})/i);
    if (ratingMatch) {
      let rating = parseInt(ratingMatch[1], 10);
      // Convert 1-10 scale to 1-100
      if (rating <= 10) rating *= 10;
      if (rating >= 1 && rating <= 100) {
        logIntent.rating = rating;
      }
    }

    // Try to extract cigar name (look for brand names)
    const brandPatterns = CIGARS.map(c => c.brand.toLowerCase()).filter((v, i, a) => a.indexOf(v) === i);
    for (const brand of brandPatterns) {
      if (text.includes(brand)) {
        // Find the cigar line too if possible
        const cigar = CIGARS.find(c =>
          text.includes(c.brand.toLowerCase()) &&
          (text.includes(c.line.toLowerCase()) || !c.line)
        );
        if (cigar) {
          logIntent.cigarName = `${cigar.brand} ${cigar.line}`;
        } else {
          logIntent.cigarName = brand;
        }
        break;
      }
    }

    // Extract notes (everything after "notes" or descriptive words)
    const notesMatch = text.match(/notes?[:\s]+(.+)/i) ||
                       text.match(/tast(?:es?|ing)\s+(?:like\s+)?(.+)/i);
    if (notesMatch) {
      logIntent.notes = notesMatch[1].trim();
    }

    return logIntent;
  }

  // 3. Default to search intent
  const searchIntent: SearchIntent = {
    type: 'search',
    query: text,
    filters: {},
    results: [],
    confidence: 0.7,
    raw: transcript,
  };

  // Extract strength filter
  for (const [keyword, strength] of Object.entries(STRENGTH_KEYWORDS)) {
    if (text.includes(keyword)) {
      searchIntent.filters.strength = strength;
      break;
    }
  }

  // Extract flavor filter
  for (const flavor of FLAVOR_KEYWORDS) {
    if (text.includes(flavor)) {
      searchIntent.filters.flavor = flavor;
      break;
    }
  }

  // Search cigars
  if (searchIntent.filters.strength && searchIntent.filters.flavor) {
    // Combined filter
    const byStrength = getCigarsByStrength(searchIntent.filters.strength as Cigar['strength']);
    searchIntent.results = byStrength.filter(c =>
      c.flavorNotes.some(f => f.toLowerCase().includes(searchIntent.filters.flavor!)) ||
      c.primaryFlavors.some(f => f.toLowerCase().includes(searchIntent.filters.flavor!))
    );
  } else if (searchIntent.filters.strength) {
    searchIntent.results = getCigarsByStrength(searchIntent.filters.strength as Cigar['strength']);
  } else if (searchIntent.filters.flavor) {
    searchIntent.results = getCigarsByFlavor(searchIntent.filters.flavor);
  } else {
    // General search
    searchIntent.results = searchCigars(text);
  }

  // If no results but we have a query, keep it as search for fallback
  if (searchIntent.results.length === 0 && text.length > 3) {
    // Try a broader search
    const words = text.split(/\s+/).filter(w => w.length > 2);
    for (const word of words) {
      const results = searchCigars(word);
      if (results.length > 0) {
        searchIntent.results = results;
        searchIntent.query = word;
        break;
      }
    }
  }

  // If still nothing, return unknown
  if (searchIntent.results.length === 0 && !searchIntent.filters.strength && !searchIntent.filters.flavor) {
    return {
      type: 'unknown',
      confidence: 0.3,
      raw: transcript,
      suggestion: 'Try asking about cigar strength, flavors, or say "show my humidor"',
    };
  }

  return searchIntent;
}
